import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HeroSection from './components/HeroSection';
import FeaturesSection from './components/FeaturesSection';
import GallerySection from './components/GallerySection';
import StatsSection from './components/StatsSection';
import TestimonialsSection from './components/TestimonialsSection';
import ProBanner from './components/ProBanner';
import FAQSection from './components/FAQSection';
import CTASection from './components/CTASection';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const homeJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Free AI Image Generator',
    url: SITE_URL,
    description: 'Create stunning AI art for free with unlimited text to image and image to image generation. No login, no signup, no limits.',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/generator?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Free AI Image Generator',
    url: SITE_URL,
    description: 'Create stunning AI art for free with unlimited text to image and image to image generation. No login, no signup, no limits.',
    applicationCategory: 'DesignApplication',
    operatingSystem: 'Web',
    inLanguage: 'en',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      ratingCount: '25017',
      bestRating: '5',
      worstRating: '1',
    },
    featureList: [
      'Text to Image Generation',
      'Image to Image Transformation',
      'Style Transfer',
      'No Login Required',
      'Unlimited Free Usage',
      'Commercial License',
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'AI Image Generator',
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: 'https://public.readdy.ai/ai/img_res/ad9c6774-e7a2-41a3-93ee-087246156af8.png',
      width: 512,
      height: 512,
    },
    sameAs: [],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: SITE_URL,
      },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is this AI Image Generator and how does it work?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Our AI Image Generator is the world\'s first completely free, unlimited AI image creation tool. It uses advanced AI models to create high-quality images from text descriptions, with no registration or usage limits required.',
        },
      },
      {
        '@type': 'Question',
        name: 'Is this AI Image Generator really free to use?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, our AI Image Generator is completely free to use! We are committed to being the world\'s largest and most powerful free AI image generator. No hidden fees, no credit cards, and no usage limits.',
        },
      },
      {
        '@type': 'Question',
        name: 'Do I need to create an account to use this?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'No account needed! Our AI Image Generator lets you visit and start generating images immediately. We believe in making AI accessible without any barriers.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I use the generated images for commercial purposes?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, you own the rights to images generated using our AI Image Generator. You can use them for both personal and commercial purposes, making it perfect for creators and businesses.',
        },
      },
      {
        '@type': 'Question',
        name: 'What types of images can I create?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Our AI Image Generator allows you to create various types of images including realistic scenes, artistic illustrations, digital art, anime-style images, and more.',
        },
      },
      {
        '@type': 'Question',
        name: 'How does this protect my privacy?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We take privacy very seriously. We do not store your prompts or generated images on our servers, and we don\'t require any personal information. Your creations remain completely confidential.',
        },
      },
      {
        '@type': 'Question',
        name: 'What makes this different from other AI image generators?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Our AI Image Generator is the only platform offering unlimited free usage with intelligent routing across multiple AI models. We provide superior image quality, fast generation speeds, and complete privacy protection, all at no cost and without registration requirements.',
        },
      },
      {
        '@type': 'Question',
        name: 'Is this available on mobile devices?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Currently, you can access our AI Image Generator through our website, which works well on mobile browsers. We are actively developing dedicated mobile apps to provide an even better experience.',
        },
      },
    ],
  },
];

export default function HomePage() {
  useEffect(() => {
    document.title = 'Free AI Image Generator | Text to Image, No Login Required 2025';

    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Create stunning AI art for free with unlimited text to image and image to image generation. No login, no signup, no limits. Best free AI art generator 2025 — commercial use allowed.');

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) metaKeywords.setAttribute('content', 'free AI image generator, text to image, AI art generator, no login AI image, free AI art, image generation online');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${SITE_URL}/`);

    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', `${SITE_URL}/`);

    let twitterUrl = document.querySelector('meta[name="twitter:url"]');
    if (twitterUrl) twitterUrl.setAttribute('content', `${SITE_URL}/`);

    let lastMod = document.getElementById('last-modified-meta');
    if (lastMod) lastMod.setAttribute('content', new Date().toISOString().split('T')[0]);

    const existingScripts = document.querySelectorAll('script[data-schema="home"]');
    existingScripts.forEach((s) => s.remove());

    homeJsonLd.forEach((schema, i) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', 'home');
      script.setAttribute('data-index', String(i));
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-schema="home"]').forEach((s) => s.remove());
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#1a1b1e]">
      <Navbar />
      <main>
        <HeroSection />
        <FeaturesSection />
        <GallerySection />
        <StatsSection />
        <TestimonialsSection />
        <ProBanner />
        <FAQSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
}
